import {Component, EventEmitter, Input, OnInit, OnDestroy, Output, ChangeDetectorRef, ElementRef, ViewChild, ViewChildren, QueryList} from '@angular/core';
import {DocumentFile, PageImages} from "@models/document-file";
import {Box} from "@components/main/review/human-validation/models/box";
import {NavigationStep} from "@components/shared/digitization-top-nav/navigation-stepper/navigation-stepper.component";
import {LaunchDarklyService} from "../../../../../services/launchdarkly.service";
import {AZURE_DOCUMENT_INTELLIGENCE_EARLY_CLASSIFICATION_UI_FLAG} from "../../../../../utils/constants";
import {Subscription} from "rxjs";
import {DocumentFileService} from "../../../../../services/document-file.service";
import {StatementService} from "../../../../../services/statement.service";
import {finalize} from "rxjs/operators";
import {DocumentTextHighlight} from "../../../../shared/document-viewer/models/document-text-highlight";
import {CapturedMergeTable} from "../merge-table-summary-panel/merge-table-summary-panel.component";
import {PdfSearchComponent} from "../../../../shared/document-viewer/pdf-toolbar/pdf-search/pdf-search.component";

interface SearchMatch {
  ulc: { x: number, y: number };
  urc: { x: number, y: number };
  llc: { x: number, y: number };
  lrc: { x: number, y: number };
  rawText: string;
  fontSize: number;
  pageSize: { width: number, height: number };
  pageNumber: number;
}

@Component({
    selector: 'app-table-drawing',
    templateUrl: './table-drawing.component.html',
    styleUrls: ['./table-drawing.component.scss', '../shared-table-merging-header-styles.scss'],
    standalone: false
})
export class TableDrawingComponent implements OnInit, OnDestroy {
  private readonly zoomIncrement = 0.2;
  private readonly minimumImgScale = 0.3;
  private resizeHandler = () => this.setBoxDrawingWidth();

  @Input() documentFile: DocumentFile;
  @Input() navigationSteps: NavigationStep[];
  @Output() exitClicked = new EventEmitter<void>();
  @Output() saveClicked = new EventEmitter<void>();
  @Output() nextClicked = new EventEmitter<void>();
  @Output() tableMergeSelection = new EventEmitter<{ box: Box, pageIndex: number, pageImage: PageImages }>();
  currentBoxesByPage: Array<Array<Box>> = [];

  subsArr$: Subscription[] = [];
  useAzureEarlyClassificationUI: boolean = false;

  // 3-pane layout properties
  activePageTrueIndex: number = 0;
  imgScale = 1;
  boxDrawingWidth: any;
  searchQuery = '';
  capturedMergeTables: CapturedMergeTable[] = [];
  selectedMergeTable: CapturedMergeTable = null;
  @ViewChildren('sidebarThumbnail') sidebarThumbnails!: QueryList<ElementRef>;

  // PDF search state
  searchText = '';
  searchLoading = false;
  searchError = '';
  searchPerformed = false;
  searchHighlights: DocumentTextHighlight[] = [];
  activeHighlightIndex = -1;
  activeHighlightId: string = null;
  activeHighlightPageIndex = -1;
  private searchSub: Subscription;
  private highlightsPerPage: Map<number, DocumentTextHighlight[]> = new Map();
  @ViewChild(PdfSearchComponent) pdfSearchComponent?: PdfSearchComponent;

  constructor(
    private _launchDarklyService: LaunchDarklyService,
    private _documentFileService: DocumentFileService,
    private _statementService: StatementService,
    private _cdr: ChangeDetectorRef,
    private _elementRef: ElementRef,
  ) {}

  ngOnInit() {
    this.resetCurrentBoxesByPage();
    this.initFeatureFlags();
    setTimeout(() => this.setBoxDrawingWidth(), 0);
    window.addEventListener('resize', this.resizeHandler);
  }

  ngOnDestroy() {
    window.removeEventListener('resize', this.resizeHandler);
    this.searchSub?.unsubscribe();
    this.subsArr$.forEach(sub => sub?.unsubscribe());
  }

  private initFeatureFlags(): void {
    this.useAzureEarlyClassificationUI = this._launchDarklyService.flags[AZURE_DOCUMENT_INTELLIGENCE_EARLY_CLASSIFICATION_UI_FLAG];

    this.subsArr$.push(this._launchDarklyService.flagChange.subscribe(flags => {
      this.useAzureEarlyClassificationUI = flags[AZURE_DOCUMENT_INTELLIGENCE_EARLY_CLASSIFICATION_UI_FLAG];
    }));
  }

  boxesChanged({boxes, activePageIndex}: { boxes: Array<Box>, activePageIndex: number }): void {
    this.resetCurrentBoxesByPage();
    this.currentBoxesByPage[activePageIndex] = boxes;
    this.tableMergeSelection.emit({
      box: boxes[0],
      pageIndex: activePageIndex,
      pageImage: this.documentFile.pageImages[activePageIndex]
    })
  }

  boxesChangedRedesigned({boxes, activePageIndex}: { boxes: Array<Box>, activePageIndex: number }): void {
    // Clear all boxes from all pages when drawing a new table (single table restriction)
    if (boxes.length > 0) {
      this.resetCurrentBoxesByPage();
      // Don't clear capturedMergeTables here - will be cleared in extractTableTitle when new table is ready
    }
    
    this.currentBoxesByPage[activePageIndex] = boxes;
    
    if (boxes.length > 0) {
      this.extractTableTitle(boxes[0], activePageIndex);
      this.tableMergeSelection.emit({
        box: boxes[0],
        pageIndex: activePageIndex,
        pageImage: this.documentFile.pageImages[activePageIndex]
      });
    } else {
      this.capturedMergeTables = this.capturedMergeTables.filter(
        t => t.pageIndex !== activePageIndex
      );
    }
  }

  private extractTableTitle(box: Box, pageIndex: number): void {
    this.subsArr$.push(
      this._statementService.startMergeTableProcess(this.documentFile.id, box, pageIndex)
        .subscribe(data => {
          const title = data.data.cells?.[0]?.[0]?.text || 'Additional Table';
          
          const capturedTable: CapturedMergeTable = {
            title,
            pageNumber: pageIndex + 1,
            pageIndex,
            box
          };
          
          // Clear all tables and add only the new one (single table restriction)
          this.capturedMergeTables = [capturedTable];
          this.selectedMergeTable = capturedTable;
          this._cdr.detectChanges();
        }, error => {
          console.error('Error extracting table title:', error);
        })
    );
  }

  resetCurrentBoxesByPage() {
    this.currentBoxesByPage = [];
    this.documentFile?.pageImages.forEach(_ => {
      this.currentBoxesByPage.push([])
    })
  }

  // 3-pane layout methods
  zoomIn(): void {
    this.imgScale += this.zoomIncrement;
  }

  zoomOut(): void {
    if (this.imgScale > this.minimumImgScale) {
      this.imgScale -= this.zoomIncrement;
    }
  }

  mathFloor(value: number): number {
    return Math.floor(value);
  }

  goToPage(event: Event): void {
    const input = event.target as HTMLInputElement;
    const pageNum = parseInt(input?.value, 10);
    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= (this.documentFile?.pageImages?.length ?? 0)) {
      this.activePageTrueIndex = pageNum - 1;
      setTimeout(() => this.scrollSidebarToActivePage());
    }
  }

  goToPrevPage(): void {
    if (this.activePageTrueIndex > 0) {
      this.activePageTrueIndex--;
      setTimeout(() => this.scrollSidebarToActivePage());
    }
  }

  goToNextPage(): void {
    const max = (this.documentFile?.pageImages?.length ?? 1) - 1;
    if (this.activePageTrueIndex < max) {
      this.activePageTrueIndex++;
      setTimeout(() => this.scrollSidebarToActivePage());
    }
  }

  activatePageRedesigned(pageIndex: number): void {
    this.activePageTrueIndex = pageIndex;
    setTimeout(() => this.scrollSidebarToActivePage());
  }

  getCurrentPageImage(): PageImages | null {
    if (!this.documentFile?.pageImages) return null;
    return this.documentFile.pageImages.find(pi => pi.pageIndex === this.activePageTrueIndex) ?? null;
  }

  trackByPageIndex(_: number, page: PageImages): number {
    return page.pageIndex;
  }

  onSearchChange(query: string): void {
    this.searchQuery = query;
  }

  get filteredPagesToShow(): PageImages[] {
    if (!this.documentFile?.pageImages) return [];

    if (this.searchPerformed && this.searchHighlights.length > 0) {
      const matchingPageNumbers = new Set(this.searchHighlights.map(h => h.pageNumber));
      return this.documentFile.pageImages.filter(page => matchingPageNumbers.has(page.pageIndex + 1));
    }

    if (!this.searchQuery.trim()) return this.documentFile.pageImages;
    const searchNum = parseInt(this.searchQuery, 10);
    if (!isNaN(searchNum)) {
      return this.documentFile.pageImages.filter(page => (page.pageIndex + 1) === searchNum);
    }

    return this.documentFile.pageImages;
  }

  private scrollSidebarToActivePage(): void {
    const thumbnailArray = this.sidebarThumbnails?.toArray();
    if (!thumbnailArray?.length) return;

    const pages = this.filteredPagesToShow;
    const targetIdx = pages.findIndex(p => p.pageIndex === this.activePageTrueIndex);
    if (targetIdx < 0 || targetIdx >= thumbnailArray.length) return;

    const targetEl = thumbnailArray[targetIdx]?.nativeElement as HTMLElement;
    if (!targetEl) return;

    const sidebarBody = targetEl.closest('.thumbnail-sidebar-body') as HTMLElement;
    if (!sidebarBody) return;

    const targetScrollTop = targetEl.offsetTop - sidebarBody.clientHeight / 2 + targetEl.clientHeight / 2;
    sidebarBody.scrollTo({ top: Math.max(0, targetScrollTop), behavior: 'smooth' });
  }

  // PDF Search methods
  get totalMatches(): number {
    return this.searchHighlights?.length || 0;
  }

  get currentMatchNumber(): number {
    return this.activeHighlightIndex >= 0 ? this.activeHighlightIndex + 1 : 0;
  }

  private resetSearchState(keepText: boolean): void {
    if (!keepText) {
      this.searchText = '';
    }
    this.searchPerformed = false;
    this.searchHighlights = [];
    this.activeHighlightIndex = -1;
    this.activeHighlightId = null;
    this.activeHighlightPageIndex = -1;
    this.highlightsPerPage = new Map();
    this.searchError = '';
  }

  onSearchTextChange(value: string): void {
    this.searchSub?.unsubscribe();
    this.searchLoading = false;
    this.searchText = value;
    this.resetSearchState(true);
    this._cdr.detectChanges();
  }

  onSearchSubmit(): void {
    if (this.searchLoading) {
      return;
    }

    const trimmed = (this.searchText || '').trim();
    if (!trimmed) {
      this.clearSearch(true);
      return;
    }

    this.resetSearchState(true);
    this._cdr.detectChanges();

    const documentFileId = Number(this.documentFile?.id);
    if (!documentFileId || isNaN(documentFileId)) {
      this.searchError = 'Document is not available for search.';
      return;
    }

    this.searchError = '';
    this.searchLoading = true;
    this.searchSub?.unsubscribe();
    this._cdr.detectChanges();

    this.searchSub = this._documentFileService.searchPageText(documentFileId, trimmed)
      .pipe(finalize(() => {
        this.searchLoading = false;
        this._cdr.detectChanges();
      }))
      .subscribe({
        next: (resp) => this.handleSearchResponse(resp),
        error: () => {
          this.searchError = 'Unable to search the document. Please try again.';
          this.clearSearch(true);
        }
      });
  }

  handleSearchResponse(resp: any): void {
    const responseObjects = resp?.response?.objects || [];
    if (!responseObjects.length) {
      this.applySearchHighlights([]);
      return;
    }

    const matches: SearchMatch[] = [];
    responseObjects.forEach(obj => {
      if (obj?.matches?.length) {
        matches.push(...obj.matches);
      }
    });

    this.applySearchHighlights(this.buildHighlightsFromMatches(matches));
  }

  buildHighlightsFromMatches(matches: SearchMatch[]): DocumentTextHighlight[] {
    if (!matches?.length) {
      return [];
    }
    return matches.map((match, idx) => ({
      id: `highlight-${idx}`,
      pageNumber: match.pageNumber,
      pageSize: match.pageSize,
      x1: match.ulc.x,
      y1: match.ulc.y,
      x2: match.lrc.x,
      y2: match.llc.y
    }));
  }

  applySearchHighlights(highlights: DocumentTextHighlight[]): void {
    this.searchPerformed = true;
    if (!highlights.length) {
      this.searchHighlights = [];
      this.activeHighlightIndex = -1;
      this.activeHighlightId = null;
      this.activeHighlightPageIndex = -1;
      this.highlightsPerPage = new Map();
      this._cdr.detectChanges();
      return;
    }

    this.searchHighlights = highlights;
    this.buildHighlightPageCache();
    this.activeHighlightIndex = 0;
    this.activeHighlightId = this.searchHighlights[0].id;
    this.updateActiveHighlightPageIndex();
    this.searchError = '';
    this.scrollToActiveHighlight(true);
    this._cdr.detectChanges();
  }

  private buildHighlightPageCache(): void {
    this.highlightsPerPage = new Map();
    for (const h of this.searchHighlights) {
      if (!this.highlightsPerPage.has(h.pageNumber)) {
        this.highlightsPerPage.set(h.pageNumber, []);
      }
      this.highlightsPerPage.get(h.pageNumber).push(h);
    }
  }

  private updateActiveHighlightPageIndex(): void {
    if (this.activeHighlightIndex >= 0 && this.activeHighlightIndex < this.searchHighlights.length) {
      this.activeHighlightPageIndex = this.searchHighlights[this.activeHighlightIndex].pageNumber - 1;
    } else {
      this.activeHighlightPageIndex = -1;
    }
  }

  getHighlightsForCurrentPage(): DocumentTextHighlight[] {
    return this.highlightsPerPage.get(this.activePageTrueIndex + 1) || [];
  }

  getDisplayHighlightsForCurrentPage(): Array<{id: string, left: number, top: number, width: number, height: number}> {
    const highlights = this.getHighlightsForCurrentPage();
    if (!highlights.length) return [];

    const renderedWidth = this.boxDrawingWidth * this.imgScale;
    return highlights.map(h => {
      const pageWidth = h.pageSize?.width || 1;
      const scale = renderedWidth / pageWidth;
      return {
        id: h.id,
        left: h.x1 * scale,
        top: h.y1 * scale,
        width: (h.x2 - h.x1) * scale,
        height: (h.y2 - h.y1) * scale
      };
    });
  }

  clearSearch(keepText: boolean = false): void {
    this.searchSub?.unsubscribe();
    this.searchLoading = false;
    this.pdfSearchComponent?.resetSearchSubject();
    this.resetSearchState(keepText);
    this._cdr.detectChanges();
  }

  goToNextMatch(): void {
    if (!this.searchHighlights.length) {
      return;
    }
    this.setActiveHighlightIndex(this.activeHighlightIndex + 1);
  }

  goToPreviousMatch(): void {
    if (!this.searchHighlights.length) {
      return;
    }
    this.setActiveHighlightIndex(this.activeHighlightIndex - 1);
  }

  private setActiveHighlightIndex(nextIndex: number): void {
    if (!this.searchHighlights.length) {
      this.activeHighlightIndex = -1;
      this.activeHighlightId = null;
      this.activeHighlightPageIndex = -1;
      this._cdr.detectChanges();
      return;
    }

    const normalizedIndex = ((nextIndex % this.searchHighlights.length) + this.searchHighlights.length) % this.searchHighlights.length;
    this.activeHighlightIndex = normalizedIndex;
    this.activeHighlightId = this.searchHighlights[normalizedIndex].id;
    this.updateActiveHighlightPageIndex();
    this.scrollToActiveHighlight(false);
    this._cdr.detectChanges();
  }

  scrollToActiveHighlight(forceCenter = false): void {
    if (this.activeHighlightId === null) {
      return;
    }
    if (this.activeHighlightPageIndex >= 0 && this.activePageTrueIndex !== this.activeHighlightPageIndex) {
      this.activePageTrueIndex = this.activeHighlightPageIndex;
    }
    setTimeout(() => this.scrollSidebarToActivePage());
    this.scrollToHighlightElement(forceCenter, 3);
  }

  private scrollToHighlightElement(forceCenter: boolean, retriesLeft: number): void {
    const delay = retriesLeft >= 3 ? 0 : 250;
    setTimeout(() => {
      if (this.activeHighlightId === null) {
        return;
      }
      const highlightEl = this._elementRef.nativeElement.querySelector(
        `[data-highlight-id="${this.activeHighlightId}"]`
      );
      if (highlightEl) {
        highlightEl.scrollIntoView({
          behavior: 'auto',
          block: forceCenter ? 'center' : 'nearest',
          inline: forceCenter ? 'center' : 'nearest'
        });
      } else if (retriesLeft > 0) {
        this.scrollToHighlightElement(forceCenter, retriesLeft - 1);
      }
    }, delay);
  }

  setBoxDrawingWidth(): void {
    const el = document.getElementById('redesigned-box-drawer-scroll-container');
    if (el) {
      this.boxDrawingWidth = el.clientWidth - 32;
    } else {
      const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
      this.boxDrawingWidth = (windowWidth / 3) - 45;
    }
  }

  onViewTableFromSummary(table: CapturedMergeTable): void {
    this.activePageTrueIndex = table.pageIndex;
    this.selectedMergeTable = table;
    setTimeout(() => this.scrollSidebarToActivePage());
  }

  trackHighlightById(index: number, item: { id: string }): string {
    return item.id;
  }

}
